import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Printer } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { PageHeader, Loading, ErrorState, EmptyState, StatusPill } from "@/components/Bits";
import { Modal, Field, inputCls, primaryBtnCls, ghostBtnCls, Table, Td } from "@/components/Kit";
import { PrintPreview } from "@/components/PrintDoc";
import { PharmacyInvoiceDoc, TEMPLATES } from "@/components/PharmacyInvoice";
import { NoAccess } from "@/components/AppShell";
import { useAuth } from "@/context/AuthContext";
import { inr, dmyTime, today } from "@/lib/format";

export default function Sales() {
  const { can } = useAuth();
  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [range, setRange] = useState({ from: "", to: "" });
  const [error, setError] = useState("");
  const [receipt, setReceipt] = useState(null);
  const [format, setFormat] = useState("classic");
  const [voidFor, setVoidFor] = useState(null);
  const [voidReason, setVoidReason] = useState("");
  const [ret, setRet] = useState(null);
  const [retQty, setRetQty] = useState({});
  const [retReason, setRetReason] = useState("");

  const load = useCallback(async () => {
    setItems(null);
    try {
      const { data } = await api.get("/pharmacy/sales", {
        params: { q: q || undefined, from_date: range.from || undefined, to_date: range.to || undefined, page, limit: 25 } });
      setItems(data.items); setTotal(data.total);
    } catch (e) { setError(apiError(e)); }
  }, [q, range, page]);

  useEffect(() => {
    if (!can("pharmacy.sales.view")) return;
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load, can]);

  if (!can("pharmacy.sales.view")) return <NoAccess what="sales history" />;

  const openReceipt = async (s) => {
    try {
      const { data } = await api.get(`/pharmacy/sales/${s.sale_id}`);
      setReceipt(data);
    } catch (e) { toast.error(apiError(e)); }
  };

  const doVoid = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/pharmacy/sales/${voidFor.sale_id}/void`, { reason: voidReason });
      toast.success("Invoice voided, stock reversed and audited.");
      setVoidFor(null); setVoidReason(""); load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const doReturn = async (e) => {
    e.preventDefault();
    const lines = Object.entries(retQty).filter(([, v]) => Number(v) > 0)
      .map(([batch_id, v]) => ({ batch_id, quantity: Number(v) }));
    if (!lines.length) return toast.error("Enter a return quantity for at least one item.");
    try {
      const { data } = await api.post("/pharmacy/sales-returns", { sale_id: ret.sale_id, items: lines, reason: retReason });
      toast.success(`Credit note ${data.sales_return.credit_note_no} · refund ${inr(data.sales_return.refund)}`);
      setRet(null); setRetQty({}); setRetReason(""); load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const quick = (days) => {
    const to = today();
    const from = new Date(Date.now() - days * 86400000).toISOString().slice(0, 10);
    setRange({ from, to }); setPage(1);
  };

  return (
    <div data-testid="sales-page">
      <PageHeader overline="Pharmacy" title="Sales history"
        subtitle="Search, reprint, return or void invoices. Sensitive actions require explicit permission." />

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <input placeholder="Invoice no, customer or mobile" value={q} data-testid="sales-search-input"
          onChange={(e) => { setQ(e.target.value); setPage(1); }}
          className="w-full sm:w-80 rounded-lg border border-slate-300 px-4 py-2 text-sm outline-none focus:border-slate-900" />
        <input type="date" value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })}
          data-testid="sales-from-input" className="rounded-lg border border-slate-300 px-3 py-2 text-sm" />
        <input type="date" value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })}
          data-testid="sales-to-input" className="rounded-lg border border-slate-300 px-3 py-2 text-sm" />
        <button onClick={() => quick(0)} className={ghostBtnCls} data-testid="sales-today-btn">Today</button>
        <button onClick={() => quick(7)} className={ghostBtnCls} data-testid="sales-week-btn">Week</button>
        <button onClick={() => quick(30)} className={ghostBtnCls} data-testid="sales-month-btn">Month</button>
        <button onClick={() => { setRange({ from: "", to: "" }); setQ(""); }} className={ghostBtnCls} data-testid="sales-clear-btn">Clear</button>
      </div>

      {error && <ErrorState message={error} />}
      {!items ? <Loading /> : (
        <>
          <Table testid="sales-table" columns={["Invoice", "Customer", "Items", "Total", "Mode", "Cashier", "Date", "Status", "Actions"]}
            rows={items} empty={<EmptyState title="No invoices found" subtitle="Adjust the filters or bill a customer from the POS." testid="sales-empty" />}
            renderRow={(s) => (
              <tr key={s.sale_id} data-testid={`sale-row-${s.sale_id}`}>
                <Td className="font-mono-num text-slate-900">{s.invoice_no}</Td>
                <Td className="font-medium text-slate-900">{s.customer_name}<span className="block text-xs text-slate-400">{s.customer_phone || "—"}</span></Td>
                <Td className="text-slate-600">{s.items.length}</Td>
                <Td className="text-slate-900">{inr(s.total)}</Td>
                <Td className="text-slate-600 capitalize">{s.payment_mode}</Td>
                <Td className="text-slate-600">{s.cashier || "—"}</Td>
                <Td className="text-slate-500 whitespace-nowrap">{dmyTime(s.created_at)}</Td>
                <Td><StatusPill status={s.status} /></Td>
                <Td>
                  <div className="flex flex-wrap gap-2">
                    {can("pharmacy.sales.reprint") && (
                      <button onClick={() => openReceipt(s)} className={ghostBtnCls} data-testid={`reprint-${s.sale_id}`}>View / reprint</button>
                    )}
                    {can("pharmacy.sales.return") && s.status !== "void" && (
                      <button onClick={() => { setRet(s); setRetQty({}); }} className={ghostBtnCls} data-testid={`return-${s.sale_id}`}>Return</button>
                    )}
                    {can("pharmacy.sales.void") && s.status === "completed" && (
                      <button onClick={() => setVoidFor(s)} className={ghostBtnCls} data-testid={`void-${s.sale_id}`}>Void</button>
                    )}
                  </div>
                </Td>
              </tr>
            )} />
          <div className="mt-5 flex items-center justify-between text-sm text-slate-500">
            <span>{total} invoice(s)</span>
            <div className="flex gap-3">
              <button disabled={page === 1} onClick={() => setPage((p) => p - 1)} className={ghostBtnCls} data-testid="sales-prev-btn">Previous</button>
              <button disabled={page * 25 >= total} onClick={() => setPage((p) => p + 1)} className={ghostBtnCls} data-testid="sales-next-btn">Next</button>
            </div>
          </div>
        </>
      )}

      <PrintPreview open={Boolean(receipt)} onClose={() => setReceipt(null)}
        title={`Invoice ${receipt?.sale?.invoice_no || ""}`}
        subtitle={TEMPLATES.find((t) => t.value === format)?.label}
        testid="reprint-preview">
        <div className="no-print mb-6 flex flex-wrap gap-2">
          {TEMPLATES.map((t) => (
            <button key={t.value} onClick={() => setFormat(t.value)}
              data-testid={`reprint-template-${t.value}`}
              className={`rounded-full border px-4 py-1.5 text-xs font-medium ${
                format === t.value ? "border-slate-900 bg-slate-900 text-white" : "border-slate-300 text-slate-600"}`}>
              {t.label}
            </button>
          ))}
        </div>
        <PharmacyInvoiceDoc sale={receipt?.sale} business={receipt?.business} template={format} />
      </PrintPreview>

      <Modal open={Boolean(voidFor)} onClose={() => setVoidFor(null)} title="Void invoice"
        subtitle="Stock is restored and the reversal is recorded. The invoice is never deleted." testid="void-sale-modal">
        <form onSubmit={doVoid} className="space-y-5">
          <Field label="Reason *"><input required minLength={3} data-testid="void-sale-reason-input" className={inputCls} value={voidReason} onChange={(e) => setVoidReason(e.target.value)} /></Field>
          <button className={primaryBtnCls} data-testid="void-sale-submit-btn">Void invoice</button>
        </form>
      </Modal>

      <Modal open={Boolean(ret)} onClose={() => setRet(null)} wide title="Sales return"
        subtitle={ret ? `Invoice ${ret.invoice_no} · ${ret.customer_name}` : ""} testid="sales-return-modal">
        {ret && (
          <form onSubmit={doReturn} className="space-y-6">
            <div className="space-y-3">
              {ret.items.map((l) => {
                const remaining = l.quantity - (l.returned || 0);
                return (
                  <div key={l.batch_id} className="grid gap-3 sm:grid-cols-[1fr_120px] items-center rounded-lg border border-slate-200 p-4">
                    <div>
                      <p className="text-sm font-medium text-slate-900">{l.medicine_name}</p>
                      <p className="text-xs text-slate-500">Batch {l.batch_no} · sold {l.quantity} · returnable {remaining}</p>
                    </div>
                    <input type="number" min={0} max={remaining} disabled={remaining === 0}
                      data-testid={`return-qty-${l.batch_id}`} placeholder="0" className={inputCls}
                      value={retQty[l.batch_id] || ""} onChange={(e) => setRetQty({ ...retQty, [l.batch_id]: e.target.value })} />
                  </div>
                );
              })}
            </div>
            <Field label="Reason *"><input required minLength={3} data-testid="return-reason-input" className={inputCls} value={retReason} onChange={(e) => setRetReason(e.target.value)} /></Field>
            <button className={primaryBtnCls} data-testid="return-submit-btn">Process return & credit note</button>
          </form>
        )}
      </Modal>
    </div>
  );
}
